// SAM-198 — «Existing client» in Client booking: find an app user to book on behalf of, so the
// service booking sends `on_behalf_of: {user_id}` instead of minting a new guest
// (booking_orchestration._resolve_on_behalf_of_precheck: `user_id` wins over `main_guest`).
//
// Pure helpers only. The search reads GET /crud/user, the same table and `<column>_search`
// (case-insensitive "contains") filters the Users pages already use (useUsers: name_search,
// email_search, phone_search). The CRUD lambda ANDs every filter of one request, so a single
// search box becomes a few requests whose results are merged:
//   - with "@"            -> email
//   - mostly digits       -> phone (`tel`) and cédula / document (`idnum`), with and without dashes
//   - words               -> first name, last name, and "first last" split across both

export interface ClientRow {
  id?: number | string;
  name?: string | null;
  last_name?: string | null;
  email?: string | null;
  tel?: string | null;
  idnum?: string | null;
  user_type?: string | null;
}

export const CLIENT_SEARCH_MIN_CHARS = 3;
export const CLIENT_SEARCH_LIMIT = 10;

/** The /crud/user param sets to request for what the operator typed; [] when too short to search. */
export function clientSearchQueries(input: string): Array<Record<string, string>> {
  const q = input.trim().replace(/\s+/g, ' ');
  if (q.length < CLIENT_SEARCH_MIN_CHARS) return [];
  if (q.includes('@')) return [{ email_search: q }];
  const digits = q.replace(/\D/g, '');
  if (digits.length >= CLIENT_SEARCH_MIN_CHARS && digits.length >= q.replace(/\s/g, '').length - 3) {
    const out: Array<Record<string, string>> = [{ tel_search: digits }, { idnum_search: digits }];
    const raw = q.replace(/\s/g, '');
    if (raw !== digits && /^[\d-]+$/.test(raw)) out.push({ idnum_search: raw });
    return out;
  }
  const words = q.split(' ');
  const out: Array<Record<string, string>> = [{ name_search: q }, { last_name_search: q }];
  if (words.length > 1) out.push({ name_search: words[0], last_name_search: words.slice(1).join(' ') });
  return out;
}

/** Results of every request, deduplicated by id (first seen wins), sorted by full name, capped. */
export function mergeClientResults(lists: ClientRow[][], limit = CLIENT_SEARCH_LIMIT): ClientRow[] {
  const seen = new Set<string>();
  const out: ClientRow[] = [];
  lists.flat().forEach((row) => {
    if (!row || row.id === undefined || row.id === null || row.id === '') return;
    const key = String(row.id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out.sort((a, b) => clientName(a).localeCompare(clientName(b))).slice(0, limit);
}

export const clientName = (row: ClientRow): string =>
  [row.name, row.last_name].map((s) => (s || '').trim()).filter(Boolean).join(' ') || 'No name';

/** "+1 809… · ana@x.com · ID 001-…" — whatever the row has, for telling two Ana Pérez apart. */
export function clientDetail(row: ClientRow): string {
  return [row.tel, row.email, row.idnum ? `ID ${row.idnum}` : '']
    .map((s) => (s || '').trim()).filter(Boolean).join(' · ');
}

/** The id as the orchestrator stores it: a number when it is one. */
export function clientUserId(row: Pick<ClientRow, 'id'>): number | string | null {
  if (row.id === undefined || row.id === null || row.id === '') return null;
  const n = Number(row.id);
  return Number.isInteger(n) && String(n) === String(row.id).trim() ? n : String(row.id);
}

/** Rows out of a /crud/user response: `{data: [...]}` or a bare array. */
export function clientRowsOf(body: unknown): ClientRow[] {
  const b = body as { data?: unknown } | unknown[] | null;
  if (Array.isArray(b)) return b as ClientRow[];
  return Array.isArray(b?.data) ? (b!.data as ClientRow[]) : [];
}
